import { fromUnixTime } from "date-fns/esm";
import { useCallback } from "react";

import { Cross, Tick } from "components/icons/generated";
import KeyValue from "components/KeyValue";
import Banner from "ds/components/Banner";
import LinkButton from "ds/components/Button/LinkButton";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardCollapsibleHeader from "ds/components/Card/Collapsible/components/Header";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import CardContent from "ds/components/Card/components/Content";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import FeedbackActions from "ds/components/Feedback/Actions";
import Icon from "ds/components/Icon";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { SCHEDULING_TYPE } from "shared/Stack/Scheduling/types";
import { ScheduledDelete } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { StackContext } from "views/Stack/Context";

import ScheduledDateDisplay from "../components/ScheduledDateDisplay";
import { StackSchedulingContextApi } from "../Context";
import { showStackManageScheduledDeleteConfirmationModal } from "./ConfrimationModal";
import useRemoveScheduledDelete from "./useRemoveScheduledDelete";

type StackManageScheduledDeleteListItemProps = {
  integration: ScheduledDelete;
  hasPrivateWorkerPool: boolean;
  isStackBlocked: boolean;
};

const StackManageScheduledDeleteListItem = ({
  integration,
  hasPrivateWorkerPool,
  isStackBlocked,
}: StackManageScheduledDeleteListItemProps) => {
  const [isCollapsed, toggleState] = useToggle(false);
  const toggle = useCallback(() => toggleState(), [toggleState]);
  const { canManageStackAndRuns } = useTypedContext(StackContext);
  const { onEdit } = useTypedContext(StackSchedulingContextApi);

  const { removeScheduledDelete, loading } = useRemoveScheduledDelete();

  const onDelete = useCallback(() => {
    removeScheduledDelete(integration.id);
  }, [removeScheduledDelete, integration]);

  const handleOnConfirm = useCallback(() => {
    showStackManageScheduledDeleteConfirmationModal({
      onConfirm: onDelete,
    });
  }, [onDelete]);

  const handleEdit = useCallback(() => onEdit(integration), [onEdit, integration]);

  const isBlocked = !hasPrivateWorkerPool || isStackBlocked;

  return (
    <CardCollapsibleWrapper isCollapsed={isCollapsed} onToggle={toggle}>
      <CardCollapsibleHeader
        ariaLevel={3}
        ariaLabel="Delete stack"
        suffix={
          canManageStackAndRuns.result && (
            <DropdownMenuEllipsis tooltip="Scheduled delete actions" buttonVariant="ghost">
              <DropdownMenuItem onAction={handleEdit}>Edit</DropdownMenuItem>
              <DropdownMenuItem
                loading={loading}
                onAction={handleOnConfirm}
                danger
                analyticsPage={AnalyticsPageStack.StackScheduling}
                analyticsTitle="Schedule Delete Clicked"
                analyticsProps={{ type: SCHEDULING_TYPE.DELETE_STACK }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          )
        }
      >
        Delete stack
      </CardCollapsibleHeader>

      {isBlocked && (
        <CardContent hasHeader>
          <Banner
            variant="danger"
            title="Scheduled Delete Stack jobs for a given stack are not scheduling:"
            fullWidth
          >
            {!hasPrivateWorkerPool && (
              <>
                The stack uses an unsupported public worker pool.
                <br />
              </>
            )}
            {isStackBlocked && (
              <>
                The stack is currently being blocked by another run.
                <br />
              </>
            )}

            <FeedbackActions>
              <LinkButton
                variant="secondary"
                size="small"
                href={getDocsUrl("/concepts/stack/scheduling#scheduling-stack-actions")}
                rel="noopener noreferrer"
                target="_blank"
              >
                Read more
              </LinkButton>
            </FeedbackActions>
          </Banner>
        </CardContent>
      )}

      <CardCollapsibleContent hasHeader>
        <KeyValue name="Scheduled date">
          {integration.timestampSchedule && (
            <ScheduledDateDisplay date={fromUnixTime(integration.timestampSchedule)} />
          )}
        </KeyValue>

        <KeyValue name="Delete resources">
          {integration.shouldDeleteResources ? (
            <Icon src={Tick} color="success" />
          ) : (
            <Icon src={Cross} color="danger" />
          )}
        </KeyValue>
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
};

export default StackManageScheduledDeleteListItem;
